import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  AddPhotoAlternate as AddPhotoIcon,
  Clear as ClearIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import AIImageGenerator from './AIImageGenerator';

interface CoverPhotoUploadProps {
  initialImageUrl?: string | null;
  onChange: (imageUrl: string | null, file: File | null) => void;
  entityId: string;
  folder?: string;
  eventTitle?: string;
  eventDescription?: string;
  eventType?: string;
}

const DEFAULT_EVENT_IMAGE = '/assets/images/placeholder-event.jpg';

const CoverPhotoUpload: React.FC<CoverPhotoUploadProps> = ({
  initialImageUrl,
  onChange,
  entityId,
  folder = 'events/covers',
  eventTitle = 'Event',
  eventDescription = '',
  eventType = ''
}) => {
   const [loading, setLoading] = useState(false);
   const [progress, setProgress] = useState(0);
   const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | null>(initialImageUrl || null);
   const [previewUrl, setPreviewUrl] = useState<string | null>(initialImageUrl || null);
   const [file, setFile] = useState<File | null>(null);
   const [error, setError] = useState<string | null>(null);
   const [showAIGenerator, setShowAIGenerator] = useState(false);

   const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const selectedFile = files[0];

      // Validate file size (150MB limit)
      const MAX_FILE_SIZE = 150 * 1024 * 1024;
      if (selectedFile.size > MAX_FILE_SIZE) {
         setError(
            `File size exceeds 150MB limit. Current size: ${(selectedFile.size / 1024 / 1024).toFixed(2)}MB`
         );
         return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(selectedFile.type)) {
         setError(`Invalid file type. Supported types: ${validTypes.join(', ')}`);
         return;
      }

      try {
         setError(null);
         setFile(selectedFile);

         // Create preview
         const reader = new FileReader();
         reader.onloadend = () => {
            setPreviewUrl(reader.result as string);
         };
         reader.readAsDataURL(selectedFile);

         // Start upload
         //  setLoading(true);
         onChange(reader.result as string, selectedFile);
         // await handleUpload(selectedFile);
      } catch (err: any) {
         setError(err.message || 'Failed to process file');
         //  setLoading(false);
      }
   };

   const handleClear = () => {
      setFile(null);
      setPreviewUrl(null);
      setCoverPhotoUrl(null);
      setError(null);
      onChange(null, null);
   };

   // NEW: Handle AI image generation
   const handleOpenAIGenerator = () => {
      setShowAIGenerator(true);
   };

   const handleCloseAIGenerator = () => {
      setShowAIGenerator(false);
   };

   const handleAIImageGenerated = (file: File | null, imagePreviewUrl: string | null) => {
      // Set the generated image as the new cover photo
      setFile(file);
      setPreviewUrl(imagePreviewUrl);
      setCoverPhotoUrl(null); // Clear existing URL since we have a new file
      onChange(null, file); // Notify parent component
   };

   return (
      <Box sx={{ mb: 3 }}>
         <Typography variant='subtitle1' gutterBottom>
            Cover Photo
         </Typography>

         <Paper
            variant='outlined'
            sx={{
               position: 'relative',
               height: 220,
               overflow: 'hidden',
               borderRadius: 1,
               bgcolor: 'background.default',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               backgroundImage: previewUrl
                  ? `url(${previewUrl})`
                  : coverPhotoUrl
                    ? `url(${coverPhotoUrl})`
                    : 'none',
               backgroundSize: 'cover',
               backgroundPosition: 'center',
            }}
         >
            {!previewUrl && !coverPhotoUrl && (
               <Box sx={{ textAlign: 'center', color: 'text.secondary', p: 2 }}>
                  <AddPhotoIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant='body2'>Add a cover photo for your event</Typography>
               </Box>
            )}

            {(previewUrl || coverPhotoUrl) && (
               <IconButton
                  size='small'
                  onClick={handleClear}
                  sx={{
                     position: 'absolute',
                     top: 8,
                     right: 8,
                     bgcolor: 'rgba(0, 0, 0, 0.4)',
                     color: 'white',
                     '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                     },
                  }}
               >
                  <ClearIcon />
               </IconButton>
            )}

            {loading && (
               <Box
                  sx={{
                     position: 'absolute',
                     top: 0,
                     left: 0,
                     right: 0,
                     bottom: 0,
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     justifyContent: 'center',
                     bgcolor: 'rgba(0, 0, 0, 0.5)',
                     color: 'white',
                  }}
               >
                  <CircularProgress color='inherit' size={40} sx={{ mb: 1 }} />
                  <Typography variant='body2'>Uploading... {progress}%</Typography>
               </Box>
            )}
         </Paper>

         <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
               component='label'
               variant='outlined'
               startIcon={<AddPhotoIcon />}
               disabled={loading}
            >
               {loading
                  ? `Uploading (${progress}%)`
                  : coverPhotoUrl
                    ? 'Change Photo'
                    : 'Upload Photo'}
               <input type='file' accept='image/*' hidden onChange={handleFileChange} />
            </Button>

            <Button
               variant='outlined'
               color='secondary'
               startIcon={<AutoAwesomeIcon />}
               disabled={loading}
               onClick={handleOpenAIGenerator}
            >
               Generate with AI
            </Button>
         </Box>

         {error && (
            <Typography variant='body2' color='error' sx={{ mt: 1 }}>
               {error}
            </Typography>
         )}

         {/* AI Image Generator Dialog */}
         <AIImageGenerator
            open={showAIGenerator}
            onClose={handleCloseAIGenerator}
            eventTitle={eventTitle}
            eventDescription={eventDescription}
            eventType={eventType}
            onImageGenerated={handleAIImageGenerated}
         />
      </Box>
   );
};

export default CoverPhotoUpload; 